import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { userRoleService } from '../../../services/userRoleService';

const ResetPasswordModal = ({ isOpen, onClose, user }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const validatePassword = (password) => {
    const errors = [];
    
    if (password.length < 8) {
      errors.push('Au moins 8 caractères');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('Une majuscule');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('Une minuscule');
    }
    if (!/[0-9]/.test(password)) {
      errors.push('Un chiffre');
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('Un caractère spécial');
    }
    
    return errors;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!newPassword) {
      newErrors.newPassword = 'Le mot de passe est requis';
    } else {
      const passwordErrors = validatePassword(newPassword);
      if (passwordErrors.length > 0) {
        newErrors.newPassword = `Le mot de passe doit contenir : ${passwordErrors.join(', ')}`;
      }
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Veuillez confirmer le mot de passe';
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    setErrors({});
    setSuccessMessage('');
    
    try {
      // Appel au service pour réinitialiser le mot de passe
      await userRoleService?.resetUserPassword(user?.id, newPassword);

      setSuccessMessage('Mot de passe réinitialisé avec succès !');
      
      // Réinitialiser le formulaire
      setTimeout(() => {
        setNewPassword('');
        setConfirmPassword('');
        setErrors({});
        setSuccessMessage('');
        onClose();
      }, 1500);
      
    } catch (error) {
      console.error('Erreur lors de la réinitialisation du mot de passe:', error);
      setErrors({ 
        submit: error?.message || 'Une erreur est survenue lors de la réinitialisation du mot de passe' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setNewPassword('');
      setConfirmPassword('');
      setErrors({});
      setSuccessMessage('');
      setShowPassword(false);
      setShowConfirmPassword(false);
      onClose();
    }
  };

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: '', color: '' };
    
    const errors = validatePassword(password);
    const strength = 5 - errors.length;
    
    if (strength <= 1) return { strength, label: 'Très faible', color: 'bg-red-500' };
    if (strength === 2) return { strength, label: 'Faible', color: 'bg-orange-500' };
    if (strength === 3) return { strength, label: 'Moyen', color: 'bg-yellow-500' };
    if (strength === 4) return { strength, label: 'Bon', color: 'bg-blue-500' };
    return { strength, label: 'Excellent', color: 'bg-green-500' };
  };

  const passwordStrength = getPasswordStrength(newPassword);

  const getInitials = (name) => {
    if (!name) return '??';
    return name
      ?.split(' ')
      ?.map(n => n?.[0])
      ?.join('')
      ?.toUpperCase()
      ?.slice(0, 2);
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in">
      <div className="bg-card border border-border rounded-lg shadow-modal w-full max-w-md animate-slide-in">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-500/10 text-blue-600 flex items-center justify-center">
              <Icon name="KeyRound" size={20} />
            </div>
            <h2 className="text-xl font-semibold text-foreground">
              Réinitialiser le mot de passe
            </h2>
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-2 rounded-lg hover:bg-muted transition-colors duration-200 disabled:opacity-50"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* User Info */}
          <div className="bg-muted/50 border border-border rounded-lg p-4">
            <p className="text-sm text-muted-foreground mb-3">
              Réinitialisation du mot de passe pour :
            </p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold flex-shrink-0">
                {user?.initials || getInitials(user?.name)}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-foreground truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="flex items-center gap-2 p-3 bg-success/10 border border-success/20 rounded-lg">
              <Icon name="CheckCircle2" size={18} className="text-success flex-shrink-0" />
              <p className="text-sm text-success font-medium">{successMessage}</p>
            </div>
          )}

          {/* Error Message */}
          {errors?.submit && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
              <Icon name="AlertCircle" size={18} className="text-destructive flex-shrink-0" />
              <p className="text-sm text-destructive">{errors.submit}</p>
            </div>
          )}

          {/* New Password */}
          <div className="space-y-2">
            <div className="relative">
              <Input
                label="Nouveau mot de passe"
                type={showPassword ? 'text' : 'password'}
                placeholder="Entrez le nouveau mot de passe"
                value={newPassword}
                onChange={(e) => setNewPassword(e?.target?.value)}
                error={errors?.newPassword}
                required
                disabled={isSubmitting}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-9 p-1 rounded hover:bg-muted transition-colors"
                tabIndex={-1}
              >
                <Icon 
                  name={showPassword ? 'EyeOff' : 'Eye'} 
                  size={18} 
                  className="text-muted-foreground" 
                />
              </button>
            </div>

            {/* Password Strength Indicator */}
            {newPassword && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Force du mot de passe :</span>
                  <span className={`font-medium ${
                    passwordStrength.strength <= 2 ? 'text-destructive' : 
                    passwordStrength.strength === 3 ? 'text-warning' : 
                    'text-success'
                  }`}>
                    {passwordStrength.label}
                  </span>
                </div>
                <div className="flex gap-1">
                  {[...Array(5)].map((_, index) => (
                    <div
                      key={index}
                      className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
                        index < passwordStrength.strength
                          ? passwordStrength.color
                          : 'bg-muted'
                      }`}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div className="relative">
            <Input
              label="Confirmer le mot de passe"
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirmez le nouveau mot de passe"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e?.target?.value)}
              error={errors?.confirmPassword}
              required
              disabled={isSubmitting}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-9 p-1 rounded hover:bg-muted transition-colors"
              tabIndex={-1}
            >
              <Icon 
                name={showConfirmPassword ? 'EyeOff' : 'Eye'} 
                size={18} 
                className="text-muted-foreground" 
              />
            </button>
          </div>

          {/* Password Requirements */}
          <div className="bg-muted/30 border border-border rounded-lg p-3">
            <p className="text-xs font-medium text-foreground mb-2">
              Le mot de passe doit contenir :
            </p>
            <ul className="space-y-1">
              {[
                { label: 'Au moins 8 caractères', test: newPassword.length >= 8 },
                { label: 'Une lettre majuscule', test: /[A-Z]/.test(newPassword) },
                { label: 'Une lettre minuscule', test: /[a-z]/.test(newPassword) },
                { label: 'Un chiffre', test: /[0-9]/.test(newPassword) },
                { label: 'Un caractère spécial (!@#$...)', test: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword) }
              ].map((requirement, index) => (
                <li key={index} className="flex items-center gap-2 text-xs">
                  <Icon
                    name={requirement.test ? 'CheckCircle2' : 'Circle'}
                    size={14}
                    className={requirement.test ? 'text-success' : 'text-muted-foreground'}
                  />
                  <span className={requirement.test ? 'text-success' : 'text-muted-foreground'}>
                    {requirement.label}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-4">
            <Button
              type="submit"
              variant="default"
              fullWidth
              loading={isSubmitting}
              disabled={isSubmitting || !!successMessage}
              iconName="KeyRound"
              iconPosition="left"
              iconSize={18}
            >
              {isSubmitting ? 'Réinitialisation...' : 'Réinitialiser le mot de passe'}
            </Button>
            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Annuler
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordModal;